// ADR 28.9.26d §1 — the order snackbars show in: Material's, not the product's.
//
// Android gets this from `SnackbarHostState.showSnackbar`, which queues behind
// whatever is already showing. It is display order rather than policy, so it
// is a pure reducer here and not a wasm shim. What is deliberately absent is
// Android's *replace* (`SpeakOrWarn.kt:137`, dismiss-then-show): it has no
// caller in this release and lands with its first one.

export type SnackbarDuration = 'short' | 'long' | 'indefinite'

/** `SnackbarDuration.Short` / `Long`, in ms; `indefinite` has no timer. */
export const DURATION_MS: Record<Exclude<SnackbarDuration, 'indefinite'>, number> = {
  short: 4000,
  long: 10000,
}

export interface SnackbarAction {
  label: string
  perform: () => void
}

export interface SnackbarRequest {
  message: string
  action?: SnackbarAction
  duration?: SnackbarDuration
}

export interface SnackbarEntry {
  id: number
  message: string
  action?: SnackbarAction
  duration: SnackbarDuration
}

export interface SnackbarQueueState {
  current: SnackbarEntry | null
  pending: SnackbarEntry[]
}

export type SnackbarEvent =
  | { type: 'show'; entry: SnackbarEntry }
  | { type: 'dismiss'; id: number }
  | { type: 'expire'; id: number }

export const EMPTY_QUEUE: SnackbarQueueState = { current: null, pending: [] }

/**
 * `short` without an action and `long` with one. Material defaults an
 * action-bearing snackbar to `Indefinite`, but the web draws no dismiss ✕ (a
 * new string × 31), and an indefinite entry at the head of a FIFO starves
 * every later one. Android's one action-bearing caller passes `Long` itself
 * for the same reason (`SpeakOrWarn.kt:117-122`).
 */
export function defaultDuration(request: SnackbarRequest): SnackbarDuration {
  return request.duration ?? (request.action ? 'long' : 'short')
}

function promote(state: SnackbarQueueState): SnackbarQueueState {
  const [head, ...rest] = state.pending
  return { current: head ?? null, pending: rest }
}

export function snackbarReducer(
  state: SnackbarQueueState,
  event: SnackbarEvent,
): SnackbarQueueState {
  switch (event.type) {
    case 'show':
      return state.current === null
        ? { current: event.entry, pending: state.pending }
        : { current: state.current, pending: [...state.pending, event.entry] }
    case 'dismiss':
      if (state.current?.id === event.id) return promote(state)
      if (!state.pending.some((entry) => entry.id === event.id)) return state
      return { current: state.current, pending: state.pending.filter((e) => e.id !== event.id) }
    case 'expire':
      // Only the current entry expires: a stale timer must not take down its successor.
      return state.current?.id === event.id ? promote(state) : state
  }
}
